import { DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { Modal, type ModalProps } from "./Modal";
import { Button } from "#components/button";

export interface ConfirmationModalProps extends ModalProps {
  /**
   * Event handler when user clicks confirm.
   */
  onConfirm?: () => void;
  /**
   * Event handler when user clicks cancel.
   */
  onReject?: () => void;
  message?: string;
  showCancelButton?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  onReject,
  showCancelButton = true,
  message = "This process is irreversible. Are you sure?",
  ...rest
}) => {
  const computedOnConfirm = (e: boolean) => {
    if (e) {
      onConfirm?.();
    } else {
      onReject?.();
    }
    setComputedOpen(false);
    onClose({}, "backdropClick");
  };

  const [computedOpen, setComputedOpen] = useState(open);

  useEffect(() => {
    setComputedOpen(open);
    if (!open) {
      onClose({}, "backdropClick");
    }
  }, [onClose, open]);

  return (
    <Modal
      data-testid="confirmation-modal"
      placement="center"
      open={computedOpen}
      showCloseButton={false}
      onClose={onClose}
      {...rest}
    >
      {rest.children ? rest.children : <h3>{message}</h3>}
      <DialogActions>
        {showCancelButton && (
          <Button
            data-testid="confirmation-modal-cancel-button"
            size="small"
            color="error"
            variant="outlined"
            onClick={() => computedOnConfirm(false)}
          >
            Cancel
          </Button>
        )}
        <Button
          data-testid="confirmation-modal-confirm-button"
          size="small"
          color="success"
          variant="contained"
          onClick={() => computedOnConfirm(true)}
        >
          Confirm
        </Button>
      </DialogActions>
    </Modal>
  );
};
